import { useState, useEffect, useRef } from 'react';
import { message as Message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useErrorMessage } from '../../../utils/errorMessage';
import { useSupervisionContext } from '../../../contexts/SupervisionContext/SupervisionContext';
import { useConfig } from '../utils/CreateUpdateConfig';
import { addDuration } from '../utils/addDuration';
import { handlePrevious } from '../utils/handlePrevious';
import { getOwner } from '../utils/getOwner';

/**
 * Custom hook for managing supervision form state and logic
 * Handles form validation, step navigation, owner data loading, and duration calculations
 * @hook
 *
 * @param {Object} params - The hook parameters
 * @param {Object} params.form - Ant Design form instance for field management
 * @param {string} params.purpose - Form purpose, either 'create' or 'update'
 * @param {string|number} params.OwnerId - Owner identifier for loading owner data
 * @param {Function} params.setCurrent - Function to update current step in multi-step form
 * @param {number} params.current - Current active step number
 *
 * @returns {Object} Hook return object containing:
 * @returns {Object} returns.refs - Object containing form refs
 * @returns {Object} returns.refs.animalsRef - Ref for animals data
 * @returns {Object} returns.refs.startDateRef - Ref for start date value
 * @returns {Object} returns.refs.endDateRef - Ref for end date value
 * @returns {Object} returns.states - Object containing form states and setters
 * @returns {Array} returns.states.duration - Calculated duration array
 * @returns {Function} returns.states.setDuration - Setter for duration state
 * @returns {number} returns.states.differenceDays - Number of days between dates
 * @returns {Array} returns.states.animalsFileList - List of animal photo files
 * @returns {number} returns.states.reduction - Price reduction value
 * @returns {Function} returns.states.setReduction - Setter for reduction state
 * @returns {string} returns.states.country - Selected country value
 * @returns {Function} returns.states.setCountry - Setter for country state
 * @returns {Array} returns.states.selectedAnouncePhotos - Selected announcement photos
 * @returns {Function} returns.states.setSelectedAnouncePhotos - Setter for announcement photos
 * @returns {boolean} returns.states.isFieldsLoading - Loading state for form fields
 * @returns {Function} returns.states.handlePrevious - Function to handle previous step navigation
 * @returns {Object} returns.config - Configuration object from useConfig hook
 * @returns {Function} returns.handleNextStep - Function to handle next step validation and navigation
 * @returns {Function} returns.setOwner - Function to update owner data in context
 * @returns {Function} returns.setOwnerId - Function to update owner ID in context
 */
export const useSupervisionForm = ({
  form,
  purpose,
  OwnerId,
  setCurrent,
  current
}) => {
  const { dispatchAPI } = useAuthContext();
  const { t } = useTranslation();
  const { message } = useErrorMessage();

  const {
    loadFormResources,
    owner,
    setOwner,
    setOwnerId,
    isFieldsLoading,
    supervisionsPrices,
    setPetsittingAddressFileList,
    setTypeOfDwelling,
    setEnvironmentType,
    supervisionDispatch,
    setSelectedAnouncePhotos,
    selectedAnouncePhotos
  } = useSupervisionContext();

  // Refs
  const animalsRef = useRef([]);
  const startDateRef = useRef({});
  const endDateRef = useRef({});

  // States
  const [duration, setDuration] = useState([]);
  const [differenceDays, setDifferenceDays] = useState(0);
  const [animalsFileList, setAnimalsFileList] = useState([]);
  const [reduction, setReduction] = useState(0);
  const [country, setCountry] = useState('');

  // Handle previous step
  const handlePreviousStep = () => handlePrevious({ setCurrent, current });

  // Load the owners, prices and enums read by the form fields
  useEffect(() => {
    loadFormResources();
  }, [loadFormResources]);

  // Initialize owner data for create mode
  useEffect(() => {
    if (purpose === 'create' && OwnerId) {
      getOwner({
        dispatchAPI,
        setOwner,
        message,
        OwnerId,
        setPetsittingAddressFileList,
        setEnvironmentType
      });
    }
  }, [
    purpose,
    OwnerId,
    dispatchAPI,
    setPetsittingAddressFileList,
    setEnvironmentType
  ]);

  // Set petsitting address when owner changes
  useEffect(() => {
    if (purpose === 'create') {
      form.setFieldsValue({ petsitting_address: owner?.petsitting_address });
    }
  }, [owner, purpose, form]);

  // Reset form on unmount for create mode
  useEffect(
    () => () => {
      if (purpose === 'create') supervisionDispatch({ type: 'RESET' });
      form.resetFields();
    },
    [purpose, supervisionDispatch, form]
  );

  // Handle animals file list
  useEffect(() => {
    if (!animalsRef.current.length) return;

    const photos = [];
    animalsRef.current.forEach((animal) => {
      animal.animal_photo?.forEach((photo) => {
        photos.push(photo);
      });
    });

    if (photos.length) {
      setAnimalsFileList((previousResult) => [...previousResult, ...photos]);
    }
  }, [animalsRef.current]);

  // Calculate duration when dates change
  useEffect(() => {
    addDuration({
      startDate: startDateRef.current,
      endDate: endDateRef.current,
      setDifferenceDays,
      supervisionsPrices,
      form,
      setDuration
    });
  }, [startDateRef.current, endDateRef.current, supervisionsPrices, form]);

  // Configuration hook
  const config = useConfig({
    setPetsittingAddressFileList,
    dispatchAPI,
    message,
    setSelectedAnouncePhotos,
    setSupervisionAddressData: (payload) =>
      supervisionDispatch({ type: 'SET_SUPERVISION_ADDRESS_DATA', payload }),
    startDateRef,
    endDateRef,
    animals: animalsRef,
    setReduction,
    setOwner,
    setEnvironmentType,
    setCountry,
    setAnimalsFileList,
    setTypeOfDwelling,
    form
  });

  // Handle next step
  const handleNextStep = async (action) => {
    try {
      const values = await form.validateFields();
      supervisionDispatch({ type: `SET_${action}`, payload: values });

      if (current <= 1) setCurrent((prevCurrent) => prevCurrent + 1);

      return null;
    } catch {
      return Message.error(t('supervisions.messages.required_fields'));
    }
  };

  return {
    refs: {
      animalsRef,
      startDateRef,
      endDateRef
    },
    states: {
      duration,
      setDuration,
      differenceDays,
      animalsFileList,
      reduction,
      setReduction,
      country,
      setCountry,
      selectedAnouncePhotos,
      setSelectedAnouncePhotos,
      isFieldsLoading,
      handlePrevious: handlePreviousStep
    },
    config,
    handleNextStep,
    setOwner,
    setOwnerId
  };
};
